import {isFeatureEnabled} from '@github-ui/feature-flags'
import {type SetIssueStatus, useSetIssueStatus} from '@github-ui/issue-actions/SetIssueStatusMutation'
import {HeaderAgentTasks, type HeaderAgentTasksFragment} from '@github-ui/issue-agent-tasks/HeaderAgentTasks'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {clsx} from 'clsx'
import {Suspense} from 'react'

import {TEST_IDS} from '../../constants/test-ids'
import {ContentWrapper} from '../ContentWrapper'
import type {IssueNode} from '../issue-viewer-view-query-types'
import type {OptionConfig} from '../OptionConfig'
import {HeaderBlockedBySummary} from './HeaderBlockedBySummary'
import {HeaderIssueType, SmallHeaderIssueType} from './HeaderIssueType'
import {type HeaderMenuBaseProps, StickyHeaderMenu} from './HeaderMenu'
import styles from './HeaderMetadata.module.css'
import {type HeaderParentFragment, HeaderParentTitle} from './HeaderParentTitle'
import {HeaderState, type HeaderStateFragment} from './HeaderState'
import {HeaderStatus} from './HeaderStatus'
import {HeaderSubIssueSummary, type HeaderSubIssueSummaryFragment} from './HeaderSubIssueSummary'
import {useIssueHeaderDataContext} from './IssueHeaderDataContext'
import {IssueMetadata, type IssueMetadataFragment} from './IssueMetadata'
import {type LinkedPullRequestsFragment, LinkedPullRequestsInternal} from './LinkedPullRequests'
import type {LinkedSecurityAlertsFragment} from './LinkedSecurityAlerts'
import {LinkedSecurityAlerts} from './LinkedSecurityAlerts'
import {RepositoryPill, SmallRepositoryPill} from './RepositoryPill'
import {StickyHeaderTitle} from './StickyHeaderTitle'
import {SmallTrackedBy, TrackedBy, type TrackedByFragment} from './TrackedBy'

type HeaderMetadataRepository = NonNullable<IssueNode['repository']>
type HeaderMetadataRepositoryOwner = NonNullable<HeaderMetadataRepository['owner']>
type HeaderMetadataIssueType = NonNullable<IssueNode['issueType']>

export type HeaderMetadataFragment = RelayCompat<
  Pick<
    IssueNode,
    'titleHTML' | 'url' | 'number' | 'state' | 'stateReason' | 'subIssuesSummary' | 'labels' | 'milestone'
  > & {
    readonly repository: Pick<
      HeaderMetadataRepository,
      'nameWithOwner' | 'name' | 'isArchived' | 'isPrivate' | 'isWritable'
    > & {
      readonly owner: Pick<HeaderMetadataRepositoryOwner, 'login' | '__typename'> & {
        readonly name?: string | null
      }
    }
    readonly issueType: Pick<HeaderMetadataIssueType, 'name' | 'color'> | null
    readonly duplicateOf?: HeaderStateFragment['duplicateOf']
    readonly linkedPullRequests: LinkedPullRequestsFragment['linkedPullRequests']
    readonly agentAssignments: HeaderAgentTasksFragment['agentAssignments']
    readonly closedByPullRequestsReferences: HeaderAgentTasksFragment['closedByPullRequestsReferences']
    readonly assignedActors: IssueMetadataFragment['assignedActors']
  }
>

export type HeaderMetadataProps = {
  headerMetadataData: HeaderMetadataFragment
  optionConfig: OptionConfig
  isSticky: boolean
  parent?: HeaderParentFragment | null
  trackedBySecondaryData?: TrackedByFragment
  subIssueSecondaryData?: HeaderSubIssueSummaryFragment
  linkedSecurityAlertsData?: LinkedSecurityAlertsFragment
} & HeaderMenuBaseProps

export function HeaderMetadata(props: HeaderMetadataProps) {
  const granularStatusesEnabled = isFeatureEnabled('issue_granular_statuses')
  const showGranularStatuses = granularStatusesEnabled && !props.optionConfig.isReadonly
  return showGranularStatuses ? <GranularHeaderMetadata {...props} /> : <HeaderMetadataContent {...props} />
}

function GranularHeaderMetadata(props: HeaderMetadataProps) {
  const issue = useIssueHeaderDataContext()
  const setIssueStatus = useSetIssueStatus({
    issueId: issue.id,
    parentIssueId: issue.parent?.id,
    owner: issue.repository.owner.login,
    repo: issue.repository.name,
    issueNumber: issue.number,
  })
  return <HeaderMetadataContent {...props} setIssueStatus={setIssueStatus} />
}

function HeaderMetadataContent({
  headerMetadataData: headerData,
  optionConfig,
  isSticky,
  parent,
  trackedBySecondaryData,
  subIssueSecondaryData,
  linkedSecurityAlertsData,
  setIssueStatus,
}: HeaderMetadataProps & {setIssueStatus?: SetIssueStatus}) {
  const stickyHeaderEnabled = isFeatureEnabled('issues_sticky_header')
  const {showRepositoryPill} = optionConfig

  const classNames = []
  if (optionConfig.insideSidePanel) {
    classNames.push('js-second-sticky-header')
  }
  return (
    <>
      {/* Regular header */}
      <ContentWrapper>
        <div
          data-testid={TEST_IDS.issueMetadataFixed}
          className={clsx(
            styles.metadataContainer,
            stickyHeaderEnabled && styles.metadataContainer_stickyHeader,
            !stickyHeaderEnabled && isSticky && styles.metadataContainerSticky,
          )}
        >
          <div
            className={clsx(
              styles.metadataBorder,
              stickyHeaderEnabled && styles.metadataBorder_stickyHeader,
              isFeatureEnabled('issue_inline_avatars') && styles.metadataBorder_inlineAvatars,
            )}
          >
            <div className={styles.metadataContent}>
              {setIssueStatus ? (
                <HeaderStatus isSticky={isSticky} setIssueStatus={setIssueStatus} stateData={headerData} />
              ) : (
                <HeaderState
                  isSticky={isSticky}
                  stateData={headerData}
                  repositoryNameWithOwner={headerData.repository.nameWithOwner}
                />
              )}
              <HeaderIssueType data={headerData} />
              <HeaderAgentTasks issueData={headerData} />

              <HeaderSubIssueSummary subIssuePrimaryKey={headerData} subIssueSecondaryKey={subIssueSecondaryData} />

              <HeaderBlockedBySummary
                state={headerData.state}
                repositoryNameWithOwner={headerData.repository.nameWithOwner}
              />

              <HeaderParentTitle
                parent={parent}
                optionConfig={optionConfig}
                repositoryNameWithOwner={headerData.repository.nameWithOwner}
              />

              <LinkedSecurityAlerts issueKey={linkedSecurityAlertsData} />

              <Suspense>
                <TrackedBy url={headerData.url} trackedByData={trackedBySecondaryData} />
              </Suspense>

              <LinkedPullRequestsInternal issueData={headerData} />
              {showRepositoryPill && (
                <RepositoryPill
                  repositoryPillData={headerData}
                  isAccountIssue={optionConfig.accountIssueOption?.isAccountIssue}
                />
              )}
            </div>
          </div>
        </div>
      </ContentWrapper>
      {/* "Sticky" header */}
      {!stickyHeaderEnabled && (
        <div
          data-testid={TEST_IDS.issueMetadataSticky}
          id="issue-viewer-sticky-header"
          className={clsx(
            classNames,
            styles.stickyContainer,
            isSticky ? styles.stickyContainerVisible : styles.stickyContainerHidden,
          )}
        >
          <ContentWrapper>
            <div className={styles.stickyContent}>
              {setIssueStatus ? (
                <HeaderStatus isSticky={isSticky} setIssueStatus={setIssueStatus} stateData={headerData} />
              ) : (
                <HeaderState
                  isSticky={isSticky}
                  stateData={headerData}
                  repositoryNameWithOwner={headerData.repository.nameWithOwner}
                />
              )}
              <div className={styles.titleAndMetadata}>
                <StickyHeaderTitle
                  headerTitleData={headerData}
                  scrollToTopOnClick={optionConfig.scrollToTopOnClick}
                  accountIssueOption={optionConfig.accountIssueOption}
                />
                <div
                  className={clsx(
                    styles.smallMetadataRow,
                    optionConfig.useViewportQueries
                      ? styles.smallMetadataRow_viewport
                      : styles.smallMetadataRow_container,
                  )}
                >
                  <SmallHeaderIssueType data={headerData} />
                  <HeaderAgentTasks issueData={headerData} isSticky />
                  <HeaderSubIssueSummary
                    subIssuePrimaryKey={headerData}
                    subIssueSecondaryKey={subIssueSecondaryData}
                    size="small"
                  />
                  <HeaderBlockedBySummary
                    state={headerData.state}
                    size="small"
                    repositoryNameWithOwner={headerData.repository.nameWithOwner}
                  />
                  <HeaderParentTitle
                    parent={parent}
                    optionConfig={optionConfig}
                    repositoryNameWithOwner={headerData.repository.nameWithOwner}
                    small
                  />
                  <LinkedSecurityAlerts issueKey={linkedSecurityAlertsData} isSmall />
                  <Suspense>
                    <SmallTrackedBy url={headerData.url} trackedByData={trackedBySecondaryData} />
                  </Suspense>
                  <LinkedPullRequestsInternal issueData={headerData} isSmall />
                  {showRepositoryPill && (
                    <SmallRepositoryPill
                      repositoryPillData={headerData}
                      isAccountIssue={optionConfig.accountIssueOption?.isAccountIssue}
                    />
                  )}
                </div>
              </div>
              <div>
                <StickyHeaderMenu optionConfig={optionConfig} headerMenuData={headerData} />
              </div>
            </div>
          </ContentWrapper>
        </div>
      )}
      <ContentWrapper>
        <IssueMetadata metadata={headerData} />
      </ContentWrapper>
    </>
  )
}
